import { defineComponent as _defineComponent } from 'vue';
import { createElementVNode as _createElementVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createVNode as _createVNode, createElementBlock as _createElementBlock, unref as _unref, vShow as _vShow, normalizeClass as _normalizeClass, withDirectives as _withDirectives, Fragment as _Fragment, createTextVNode as _createTextVNode, renderList as _renderList, isRef as _isRef } from "vue";
const _hoisted_1 = ["innerHTML"];
const _hoisted_2 = {
    key: 0,
    class: "rec-recorder__stage__alert"
};
const _hoisted_3 = ["innerHTML"];
const _hoisted_4 = { class: "rec-recorder__stage__content" };
const _hoisted_5 = {
    key: 0,
    class: "rec-perms-denied"
};
const _hoisted_6 = {
    key: 1,
    class: "rec-perms-denied"
};
const _hoisted_7 = { class: "rec-perms-denied__wrap" };
const _hoisted_8 = ["hidden"];
const _hoisted_9 = { class: "rec-recorder__wave-wrap" };
const _hoisted_10 = ["src", "alt"];
const _hoisted_11 = {
    key: 4,
    class: "rec-screen-overlay"
};
const _hoisted_12 = { key: 0 };
const _hoisted_13 = {
    key: 0,
    class: "rec-screen-overlay__message"
};
const _hoisted_14 = {
    key: 1,
    class: "rec-screen-overlay__message"
};
const _hoisted_15 = ["innerHTML"];
const _hoisted_16 = {
    key: 2,
    class: "rec-screen-overlay__preview"
};
const _hoisted_17 = { class: "rec-screen-overlay__preview__buttons" };
const _hoisted_18 = ["aria-label"];
const _hoisted_19 = { class: "rec-recorder__buttons" };
const _hoisted_20 = {
    key: 0,
    class: "rec-recorder__pause-overlay"
};
const _hoisted_21 = { class: "rec-recorder__buttons" };
const _hoisted_22 = { key: 0 };
const _hoisted_23 = ["innerHTML"];
const _hoisted_24 = { key: 1 };
const _hoisted_25 = {
    key: 1,
    class: "rec-recorder__blur-nav"
};
const _hoisted_26 = ["aria-label"];
const _hoisted_27 = {
    key: 0,
    class: "rec-recorder__foot__playback-controls"
};
const _hoisted_28 = {
    key: 1,
    class: "rec-recorder__foot__settings"
};
const _hoisted_29 = { class: "rec-recorder__foot__duration" };
const _hoisted_30 = ["aria-label"];
import { useConfirmDialog, useDevicesList, whenever } from "@vueuse/core";
import { computed, defineAsyncComponent, inject, nextTick, onUnmounted, ref, useTemplateRef, watch } from "vue";
import { vTooltip } from "@/common/directives/tip-directive";
import { detect } from "detect-browser";
import { useCurrentUser } from "@/store/current-user";
import { devMode } from "@/config";
// Components
import AlertMessage from "@/common/components/AlertMessage.vue";
import SvgIcon from "@/common/components/SvgIcon.vue";
import DeviceInputSelect from "../components/DeviceInputSelect.vue";
import RecordButton from "../components/RecordButton.vue";
import RecorderDuration from "../components/RecorderDuration.vue";
import MediaPlayer from "../modules/MediaPlayer.vue";
// Composables
import { useAudioMeter } from "../composables/audio-meter";
import { useMediaRecorder } from "../composables/media-recorder";
import { useMediaStream } from "../composables/media-stream";
import { useStreamWaveform } from "../composables/stream-waveform";
import { useVideoBackground } from "../composables/video-background";
import { useBrowserTabFlash } from "@/common/composables/browser-tab-flash";
// Types & enums
import { MediaSource } from "../types";
export default /*@__PURE__*/ _defineComponent({
    __name: 'MediaRecorderView',
    props: {
        minDuration: {},
        maxDuration: {},
        mobile: { type: Boolean },
        saveCallback: { type: Function },
        uploadCallback: { type: Function },
        recordingCallback: { type: Function },
        cancelCallback: { type: Function },
        type: {},
        screenUrl: {},
        noVideoDisable: { type: Boolean }
    },
    setup(__props) {
        // Scripts
        const i18n = inject("i18n");
        const DiscardRecordingModal = defineAsyncComponent(() => import("../components/DiscardRecordingModal.vue"));
        const browser = detect();
        const props = __props;
        const isAudio = computed(() => props.type === MediaSource.Audio);
        const isVideo = computed(() => props.type === MediaSource.Video);
        const isScreen = computed(() => props.type === MediaSource.Screen);
        // As of Safari 18.4, iOS supports webm. It has issues with orientation detecting incorrectly
        // in iOS 18.4.1; we should revisit this once iOS updates to fix the issue
        const bypassIosWebm = isVideo.value && browser?.os?.toLowerCase().startsWith("ios");
        const mimeType = ref(isVideo.value || isScreen.value
            ? MediaRecorder.isTypeSupported("video/webm;codecs=vp9") &&
                !browser?.os?.toLowerCase().startsWith("android") &&
                !bypassIosWebm
                ? "video/webm;codecs=vp9"
                : MediaRecorder.isTypeSupported("video/webm") && !bypassIosWebm
                    ? "video/webm"
                    : MediaRecorder.isTypeSupported("video/mp4")
                        ? "video/mp4"
                        : ""
            : MediaRecorder.isTypeSupported("audio/webm") && !bypassIosWebm
                ? "audio/webm"
                : MediaRecorder.isTypeSupported("audio/mp4")
                    ? "audio/mp4"
                    : MediaRecorder.isTypeSupported("video/webm") && !bypassIosWebm
                        ? "video/webm"
                        : MediaRecorder.isTypeSupported("video/mp4")
                            ? "video/mp4"
                            : "");
        //console.log(browser)
        //console.log("MIME=", mimeType.value)
        const mediaDeviceInfo = useDevicesList({
            requestPermissions: true,
            constraints: { audio: true, video: isVideo.value }
        });
        const devices = mediaDeviceInfo.devices;
        if (devices.value.length == 0) {
            navigator.mediaDevices.getUserMedia({ audio: true, video: isVideo.value }).then(async (s) => {
                // useDevicesList doesn't always workm Firefox requires a pull from the media service before listing devices.
                devices.value = await navigator.mediaDevices.enumerateDevices();
                s.getTracks().forEach((t) => t.stop());
            });
        }
        const audioInputs = computed(() => devices.value?.filter((a) => a.kind == "audioinput" && a.deviceId != "") || []);
        const videoInputs = computed(() => devices.value?.filter((a) => a.kind == "videoinput" && a.deviceId != "") || []);
        const recordingPaused = ref(false);
        const muted = ref(false);
        const cameraDisabled = ref(false);
        const selectingScreen = ref(false);
        const inRecordingMode = computed(() => selectingScreen.value || !recordingPaused.value || isRecording.value);
        const doPause = () => {
            console.log("toggle pause");
            recordingPaused.value = true;
            selectingScreen.value = false;
        };
        const doContinue = () => {
            if (!isScreen.value || (isScreenSharing.value && !startedShareWithAddress.value)) {
                recordingPaused.value = false;
            }
            else {
                selectingScreen.value = true;
                if (startedShareWithAddress.value) {
                    openWindow();
                }
                else {
                    startScreenSharing();
                }
            }
        };
        const resetRecording = async () => {
            const { isCanceled } = await showDiscardDraftWarning();
            if (!isCanceled) {
                resetDesktopStream();
                recordingPaused.value = false;
                if (isScreenSharing.value) {
                    stopScreenSharing();
                }
                if (isScreenWithAddress.value) {
                    startedShareWithAddress.value = false;
                }
                playbackTimer.value = 0;
                recorderReset();
            }
        };
        const canvasEl = ref();
        const { mediaStream, audioInputDeviceId, videoInputDeviceId, getDesktopStream, resetDesktopStream, resetStream, closeStreams, videoStream } = useMediaStream({
            muted,
            cameraDisabled,
            devices,
            source: props.type,
            canvasEl
        });
        const { isRecording, recordedSeconds, segmentRecordings, resetRecording: recorderReset, startRecording, saveRecording, stopRecording, savingSubmitted, recordingReady, recordingSaved, connectionError, uuid, uploadProgress } = useMediaRecorder({ mediaStream, paused: recordingPaused, mimeType });
        const audioMeter = useAudioMeter(mediaStream);
        const { startTabFlash } = useBrowserTabFlash();
        const currentUser = useCurrentUser();
        const profileImage = currentUser.thumbnails.x205;
        const { isRevealed: isDraftDiscardModalOpen, reveal: showDiscardDraftWarning, confirm: confirmDraftDiscard, cancel: cancelDraftDiscard } = useConfirmDialog();
        const warningModal = computed({
            get: () => isDraftDiscardModalOpen.value,
            set: (val) => {
                if (!val)
                    return cancelDraftDiscard();
            }
        });
        onUnmounted(() => {
            if (devMode)
                console.log("UNMOUNT");
            stopRecording(true);
            closeStreams();
        });
        const limitMessage = ref("");
        const handleLimitMessage = (newlimitMessage) => {
            limitMessage.value = newlimitMessage;
        };
        const minimumWarning = computed(() => props.minDuration <= 0
            ? ""
            : props.minDuration > 90
                ? i18n.t("Continue recording to reach minimum of {0}{1} minutes{2}.", [
                    "<strong>",
                    props.minDuration / 60,
                    "</strong>"
                ])
                : i18n.t("Continue recording to reach minimum of {0}{1} seconds{2}.", [
                    "<strong>",
                    props.minDuration,
                    "</strong>"
                ]));
        const webCamVideoDisabled = ref("<strong>" +
            i18n.t("Your webcam is inaccessible.") +
            "</strong>&nbsp;" +
            i18n.t("Please ensure you have granted permission for this site to access your camera and microphone."));
        const webCamMicDisabled = ref("<strong>" +
            i18n.t("No microphone detected.") +
            "</strong>&nbsp;" +
            i18n.t("Please ensure you have granted permission for this site to access your camera and microphone."));
        const maximumWarning = computed(() => props.maxDuration <= 0
            ? ""
            : props.maxDuration > 90
                ? i18n.t("Maximum duration of {0}{1} minutes{2} has been reached.", [
                    "<strong>",
                    props.maxDuration / 60,
                    "</strong>"
                ])
                : i18n.t("Maximum duration of {0}{1} seconds{2} has been reached.", ["<strong>", props.maxDuration, "</strong>"]));
        const noMicrophone = ref(isAudio.value
            ? i18n.t("Your browser requires access to your {0}microphone{1}.", ["<strong>", "</strong>"])
            : i18n.t("No microphone detected."));
        const connectionLost = ref(i18n.t("Connection lost, retrying now") + "...");
        const noSoundDetected = ref(i18n.t("We cannot hear you well, please check your microphone."));
        const mutedWarning = ref("<strong>" + i18n.t("Your microphone is currently muted.") + "</strong>");
        const noVideo = computed(() => isVideo.value && videoInputs.value?.length <= 0);
        const noWebcam = computed(() => isVideo.value && audioInputs.value?.length <= 0 && videoInputs.value?.length <= 0);
        const noWebcamAudio = computed(() => isVideo.value && audioInputs.value?.length <= 0);
        const noAudioDevice = computed(() => audioInputs.value?.length <= 0);
        const recordingMuted = ref(false);
        const deadMic = computed(() => audioMeter.deadAudio?.value && !recordingPaused.value);
        const atMax = computed(() => props.maxDuration > 0 && recordedSeconds.value >= props.maxDuration);
        const belowMin = computed(() => recordingPaused.value && recordedSeconds.value < props.minDuration);
        const screenShareReady = ref(false);
        const screenShareWrongTarget = ref(false);
        const warnings = ref(new Map([
            [noWebcam, { message: webCamVideoDisabled, hidden: false }],
            [noWebcamAudio, { message: webCamMicDisabled, hidden: false }],
            [noVideo, { message: webCamVideoDisabled, hidden: false }],
            [noAudioDevice, { message: noMicrophone, hidden: false }],
            [connectionError, { message: connectionLost, hidden: false }],
            [belowMin, { message: minimumWarning, hidden: false }],
            [atMax, { message: maximumWarning, hidden: false }],
            [recordingMuted, { message: mutedWarning, hidden: false }],
            [deadMic, { message: noSoundDetected, hidden: false }]
        ]));
        const currentWarning = computed(() => [...warnings.value.entries()].find((v) => v[0].value && !v[1].hidden)?.[1]);
        const showWarning = computed(() => currentWarning.value != undefined);
        const warningMessage = computed(() => currentWarning.value?.message);
        const hideWarning = () => {
            if (currentWarning.value)
                currentWarning.value.hidden = true;
        };
        [...warnings.value.keys()].forEach((k) => {
            whenever(k, () => {
                const warning = warnings.value.get(k);
                if (warning != undefined) {
                    warning.hidden = false;
                }
            });
        });
        watch([isRecording, muted, recordingPaused], ([r, m, p], [r2, _m2, _p2]) => {
            if (devMode)
                console.log(`isRecording.value = ${isRecording.value}`);
            if (isScreen.value && !r && r2 && !isScreenSharing.value) {
                stopRecording(false);
                resetDesktopStream();
            }
            if (r && m)
                recordingMuted.value = true;
            if (!m)
                recordingMuted.value = false;
            if (props.recordingCallback)
                props.recordingCallback(r || p);
        });
        whenever(atMax, () => (recordingPaused.value = true));
        whenever(recordingSaved, () => {
            if (devMode)
                console.log("recordingSaved");
            stopRecording(true);
            closeStreams();
            if (props.saveCallback)
                props?.saveCallback(uuid.value);
        });
        const isPlaying = ref(false);
        const isPlaybackPaused = ref(false);
        const playbackTimer = ref(0);
        const startPlayback = () => {
            isPlaying.value = true;
        };
        const stopPlayback = () => {
            isPlaying.value = false;
            isPlaybackPaused.value = false;
        };
        const pausePlayback = () => {
            isPlaybackPaused.value = true;
            isPlaying.value = false;
        };
        const isVideoPlayback = computed(() => (isVideo.value || isScreen.value) && (isPlaying.value || isPlaybackPaused.value));
        const handleUploadOptionClick = () => {
            if (props.uploadCallback)
                props?.uploadCallback();
        };
        const hasVideoSource = computed(() => isVideo.value || isScreen.value);
        const noAudio = computed(() => segmentRecordings.value.reduce((a, b) => a + b.file.size(), 0) <= 0);
        const recordingWaveform = ref();
        const waveformConfig = ref({
            waveColor: "#FFF",
            barWidth: 2.5,
            barRadius: 2.5,
            interact: false,
            height: 120,
            barHeight: 2,
            barMinHeight: 1,
            barGap: 5,
            responsive: true
        });
        whenever(audioMeter.streamFailed, () => {
            if (props.type == MediaSource.Audio) {
                resetStream();
            }
        });
        watch(audioMeter.analyser, () => {
            if (recordingWaveform.value) {
                stopWaveformStreamPlay();
                recordingWaveform.value.innerHTML = "";
                waveformStreamPlay(recordingWaveform, audioMeter.analyser);
            }
        });
        watch(recordingWaveform, (el) => {
            if (el) {
                waveformStreamPlay(recordingWaveform, audioMeter.analyser);
            }
            else {
                stopWaveformStreamPlay();
            }
        });
        const { stopWaveformStreamPlay, waveformStreamPlay } = useStreamWaveform(waveformConfig);
        const recorderWrapClasses = computed(() => ({
            "rec-recorder-wrap": true,
            "rec-recorder-wrap--mobile": props.mobile || window.Ramius.is_mobile
        }));
        const recorderClasses = computed(() => ({
            "rec-recorder": true,
            [`rec-recorder--${props.type}`]: true
        }));
        const stageClasses = computed(() => ({
            "rec-recorder__stage": true,
            "rec-recorder__stage--recording": inRecordingMode.value,
            "rec-recorder__stage--paused": !inRecordingMode.value,
            "rec-recorder__stage--playing": isPlaying.value || isPlaybackPaused.value
        }));
        const recordingModeClasses = computed(() => ({
            "rec-record-mode": true,
            "rec-record-mode--screen": isScreen.value,
            "rec-record-mode--audio": isAudio.value,
            "rec-record-mode--video": isVideo.value,
            "rec-record-mode--screen--recording": isScreenSharing.value,
            "rec-record-mode--screen--preview": isScreenPreview.value
        }));
        const videoPreviewEl = ref();
        const isBackCam = ref(false);
        const isBackCamera = (stream) => {
            const videoTrack = stream.getVideoTracks()[0];
            if (videoTrack) {
                const capabilities = videoTrack.getCapabilities();
                return !!(capabilities.facingMode && capabilities.facingMode.includes("environment"));
            }
            return false;
        };
        const canvasSupport = isVideo.value && browser?.os != "iOS" && !(browser?.name == "safari" && Number(browser.version.split(".")[0]) < 16);
        const canvasEnabled = ref(false);
        const backgroundBlurAmount = ref(0);
        let { createImageSegmenter } = canvasSupport && canvasEnabled.value
            ? useVideoBackground(videoPreviewEl, canvasEl, backgroundBlurAmount)
            : { createImageSegmenter: undefined };
        const showBlurCanvasIfBlurring = () => (canvasEnabled.value = backgroundBlurAmount.value !== 0);
        watch(backgroundBlurAmount, showBlurCanvasIfBlurring);
        watch([canvasEl, canvasEnabled], ([c, e]) => {
            if (e && c) {
                console.log(canvasEl);
                const a = useVideoBackground(videoPreviewEl, canvasEl, backgroundBlurAmount);
                createImageSegmenter = a.createImageSegmenter;
                if (createImageSegmenter) {
                    createImageSegmenter();
                }
            }
            else {
                createImageSegmenter = undefined;
            }
        });
        watch(videoPreviewEl, async () => {
            if (videoPreviewEl.value && mediaStream.value) {
                await new Promise((resolve) => {
                    videoPreviewEl.value.onloadedmetadata = () => {
                        resolve(videoPreviewEl.value);
                    };
                    videoPreviewEl.value.srcObject = !isVideo.value ? mediaStream.value : videoStream.value;
                });
                videoPreviewEl.value.play().catch(() => videoPreviewEl.value.play());
                if (createImageSegmenter) {
                    createImageSegmenter();
                }
            }
        });
        watch([mediaStream, videoStream], async () => {
            if (devMode)
                console.log("mediaStream change");
            if (videoPreviewEl.value && mediaStream.value) {
                await new Promise((resolve) => {
                    videoPreviewEl.value.onloadedmetadata = () => {
                        resolve(videoPreviewEl.value);
                    };
                    const currentStream = !isVideo.value ? mediaStream.value : videoStream.value;
                    videoPreviewEl.value.srcObject = currentStream;
                    isBackCam.value = isBackCamera(currentStream);
                    if (devMode)
                        console.log("is back camera: ", isBackCam.value);
                });
                if (devMode)
                    console.log("play");
                if (createImageSegmenter) {
                    createImageSegmenter();
                }
                nextTick(() => {
                    videoPreviewEl.value.play();
                });
                if (devMode)
                    console.log("play completee");
            }
        });
        // Screenshare
        const isScreenSharing = ref(false);
        const isScreenPreview = ref(false);
        const isScreenWithAddress = computed(() => (props.screenUrl?.length ?? 0) > 0);
        const startedShare = ref(false);
        const startedShareWithAddress = ref(false);
        const startScreenSharing = async () => {
            if (startedShare.value)
                return;
            try {
                if (!isScreenSharing.value) {
                    startedShare.value = true;
                    screenShareReady.value = false;
                    screenShareWrongTarget.value = false;
                    await getDesktopStream(isScreenWithAddress.value ? "window" : undefined);
                    mediaStream.value?.getVideoTracks()[0].addEventListener("ended", stopScreenSharing);
                    const selectedSurface = mediaStream.value?.getVideoTracks()[0].getSettings().displaySurface;
                    if (isScreenWithAddress.value) {
                        if (selectedSurface == "browser") {
                            // Browser tab selected. Unsupported for URL start.
                            closeStreams();
                            stopScreenSharing();
                            selectingScreen.value = false;
                            screenShareWrongTarget.value = true;
                            startTabFlash("!!!", -1, true);
                        }
                        else {
                            screenShareReady.value = true;
                            startedShareWithAddress.value = true;
                            selectingScreen.value = true;
                        }
                        return;
                    }
                    else {
                        isScreenSharing.value = true;
                        recordingPaused.value = false;
                        screenShareReady.value = true;
                        if (!isRecording.value) {
                            startRecording();
                        }
                    }
                }
                else {
                    stopScreenSharing();
                }
            }
            catch (error) {
                if (devMode)
                    console.error("Error accessing screen share:", error);
            }
            finally {
                startedShare.value = false;
            }
            selectingScreen.value = false;
        };
        const openWindow = () => {
            window.open(props.screenUrl, "_blank");
            isScreenSharing.value = true;
            recordingPaused.value = false;
            if (!isRecording.value) {
                startRecording();
            }
        };
        const newScreen = async (e) => {
            e.preventDefault();
            if (selectingScreen.value)
                return;
            selectingScreen.value = true;
            if (isScreenWithAddress.value) {
                startedShareWithAddress.value = false;
            }
            mediaStream.value?.getVideoTracks()[0].removeEventListener("ended", stopScreenSharing);
            stopScreenShare();
            startScreenSharing();
        };
        const stopScreenSharing = () => {
            selectingScreen.value = false;
            screenShareReady.value = false;
            stopScreenShare();
        };
        const stopScreenShare = () => {
            startedShareWithAddress.value = false;
            isScreenPreview.value = false;
            isScreenSharing.value = false;
            if (isRecording.value) {
                if (recordingPaused.value) {
                    stopRecording(false);
                    resetDesktopStream();
                }
                recordingPaused.value = true;
            }
        };
        const startPreviewScreenSharing = () => {
            isScreenPreview.value = true;
        };
        const stopPreviewScreenSharing = () => {
            isScreenPreview.value = false;
        };
        const showVideoPreview = computed(() => (isVideo.value && !cameraDisabled.value) || (isScreen.value && screenShareReady.value));
        const cancelRecorder = async () => {
            if (!inRecordingMode.value || isRecording.value) {
                const { isCanceled } = await showDiscardDraftWarning();
                if (isCanceled)
                    return;
            }
            stopRecording(true);
            closeStreams();
            if (props.cancelCallback)
                props.cancelCallback();
        };
        // Blur background
        const blurAmounts = [0, 4, 8];
        const blurHover = ref(false);
        const blurMouseOver = () => {
            blurHover.value = true;
        };
        const blurMouseLeave = () => {
            blurHover.value = false;
        };
        const blurLabel = computed(() => {
            if (backgroundBlurAmount?.value === blurAmounts[0]) {
                return i18n.t("No Blur");
            }
            else if (backgroundBlurAmount?.value === blurAmounts[1]) {
                return i18n.t("Slight Blur");
            }
            else if (backgroundBlurAmount?.value === blurAmounts[2]) {
                return i18n.t("Heavy Blur");
            }
            else {
                return i18n.t("No Blur");
            }
        });
        // Blur background button
        const showBlurButton = computed(() => canvasSupport && showVideoPreview.value && inRecordingMode.value && !!mediaStream.value);
        const blurLineClasses = computed(() => (amount) => ({
            "rec-recorder__blur-nav__line": true,
            "rec-recorder__blur-nav__line--active": backgroundBlurAmount?.value === amount
        }));
        const blurButtonClasses = computed(() => [
            "rec-recorder__blur",
            {
                "rec-recorder__blur--none": backgroundBlurAmount?.value === blurAmounts[0],
                "rec-recorder__blur--slight": backgroundBlurAmount?.value === blurAmounts[1],
                "rec-recorder__blur--heavy": backgroundBlurAmount?.value === blurAmounts[2]
            }
        ]);
        const blurIcon = computed(() => {
            switch (backgroundBlurAmount?.value) {
                case blurAmounts[0]:
                    return "icon-participant-blur-none";
                case blurAmounts[1]:
                    return "icon-participant-blur-slight";
                case blurAmounts[2]:
                    return "icon-participant-blur-heavy";
                default:
                    return "icon-participant-blur-none";
            }
        });
        const cycleBlurAmount = () => {
            if (!canvasEnabled.value) {
                canvasEnabled.value = true;
                nextTick(cycleBlurAmount);
                return;
            }
            const currentIndex = blurAmounts.indexOf(backgroundBlurAmount.value);
            backgroundBlurAmount.value = blurAmounts[(currentIndex + 1) % blurAmounts.length];
        };
        // Focus management for Record/Pause button transition
        const pauseButtonRef = useTemplateRef("pauseButtonRef");
        const recordButtonRef = useTemplateRef("recordButtonRef");
        watch(isRecording, (nowRecording, wasRecording) => {
            if (nowRecording && !wasRecording) {
                // Recording just started - focus the pause button
                nextTick(() => {
                    pauseButtonRef.value?.$el?.focus();
                });
            }
            else if (!nowRecording && wasRecording) {
                // Recording stopped - focus the record button
                nextTick(() => {
                    recordButtonRef.value?.$el?.focus();
                });
            }
        });
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock(_Fragment, null, [
                (limitMessage.value && !_ctx.mobile)
                    ? (_openBlock(), _createBlock(AlertMessage, {
                        key: 0,
                        type: "info",
                        class: "rc-mb20"
                    }, {
                        default: _withCtx(() => [
                            _createElementVNode("div", { innerHTML: limitMessage.value }, null, 8, _hoisted_1)
                        ]),
                        _: 1
                    }))
                    : _createCommentVNode("", true),
                (screenShareWrongTarget.value)
                    ? (_openBlock(), _createBlock(AlertMessage, {
                        key: 1,
                        type: "danger",
                        class: "rc-mb20"
                    }, {
                        default: _withCtx(() => [
                            _createElementVNode("div", null, _toDisplayString(_ctx.$t("Sharing a tab is not available.")) + " " + _toDisplayString(_ctx.$t('Please select the current "Window" or "Screen" when sharing your screen.')), 1)
                        ]),
                        _: 1
                    }))
                    : _createCommentVNode("", true),
                _createElementVNode("div", {
                    class: _normalizeClass(recorderWrapClasses.value)
                }, [
                    _createElementVNode("div", {
                        class: _normalizeClass(recorderClasses.value)
                    }, [
                        (showWarning.value)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                                _createVNode(AlertMessage, { type: "warning" }, {
                                    default: _withCtx(() => [
                                        _createElementVNode("div", { innerHTML: warningMessage.value }, null, 8, _hoisted_3),
                                        _createElementVNode("button", {
                                            type: "button",
                                            class: "btn btn-warning",
                                            onClick: hideWarning
                                        }, _toDisplayString(_ctx.$t("OK")), 1)
                                    ]),
                                    _: 1
                                })
                            ]))
                            : _createCommentVNode("", true),
                        _createElementVNode("div", {
                            class: _normalizeClass(stageClasses.value)
                        }, [
                            _withDirectives(_createElementVNode("div", _hoisted_4, [
                                _createElementVNode("div", {
                                    class: _normalizeClass(recordingModeClasses.value)
                                }, [
                                    false
                                        ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                                            _createVNode(SvgIcon, { icon: "icon-mic-off" })
                                        ]))
                                        : _createCommentVNode("", true),
                                    false
                                        ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                                            _createElementVNode("div", _hoisted_7, [
                                                _cache[8] || (_cache[8] = _createElementVNode("div", { class: "rec-perms-denied__sound-level" }, [
                                                    _createElementVNode("span", { style: { "height": "38%" } })
                                                ], -1)),
                                                _createVNode(SvgIcon, { icon: "icon-mic" })
                                            ])
                                        ]))
                                        : _createCommentVNode("", true),
                                    (hasVideoSource.value)
                                        ? _withDirectives((_openBlock(), _createElementBlock("video", {
                                            key: 2,
                                            ref_key: "videoPreviewEl",
                                            ref: videoPreviewEl,
                                            hidden: _unref(canvasSupport) && canvasEnabled.value,
                                            class: _normalizeClass({ 'back-camera': isBackCam.value }),
                                            autoplay: "",
                                            muted: "",
                                            playsinline: ""
                                        }, null, 10, _hoisted_8)), [
                                            [_vShow, showVideoPreview.value]
                                        ])
                                        : _createCommentVNode("", true),
                                    (_unref(canvasSupport) && canvasEnabled.value && hasVideoSource.value)
                                        ? _withDirectives((_openBlock(), _createElementBlock("canvas", {
                                            key: 3,
                                            ref_key: "canvasEl",
                                            ref: canvasEl,
                                            class: _normalizeClass({ 'back-camera': isBackCam.value })
                                        }, null, 2)), [
                                            [_vShow, showVideoPreview.value]
                                        ])
                                        : _createCommentVNode("", true),
                                    _withDirectives(_createElementVNode("div", _hoisted_9, [
                                        (_unref(isRecording) && !recordingPaused.value)
                                            ? (_openBlock(), _createElementBlock("div", {
                                                key: 0,
                                                ref_key: "recordingWaveform",
                                                ref: recordingWaveform,
                                                class: "rec-recorder__waveform"
                                            }, null, 512))
                                            : _createCommentVNode("", true),
                                        (_unref(profileImage))
                                            ? (_openBlock(), _createElementBlock("img", {
                                                key: 1,
                                                src: _unref(profileImage),
                                                alt: _ctx.$t('Profile Photo'),
                                                class: "rec-recorder__avatar"
                                            }, null, 8, _hoisted_10))
                                            : _createCommentVNode("", true)
                                    ], 512), [
                                        [_vShow, !hasVideoSource.value || cameraDisabled.value]
                                    ]),
                                    (isScreen.value && !isVideoPlayback.value)
                                        ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                                            (!isScreenSharing.value)
                                                ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                                                    (isScreenWithAddress.value)
                                                        ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
                                                            (!startedShareWithAddress.value)
                                                                ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                                                    _createElementVNode("h3", null, _toDisplayString(_ctx.$t("Share Your Screen")), 1),
                                                                    _createElementVNode("div", null, _toDisplayString(_ctx.$t('When prompted, select the current "Window" or "Screen".')) + " " + _toDisplayString(_ctx.$t('Once you share, return to this tab, click "Record", and the proper screen will open for you to start recording.')), 1),
                                                                    _createElementVNode("button", {
                                                                        type: "button",
                                                                        class: "btn btn-lg btn-primary",
                                                                        onClick: startScreenSharing
                                                                    }, _toDisplayString(_ctx.$t("Share Screen")), 1)
                                                                ], 64))
                                                                : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                                                    _createElementVNode("h3", null, _toDisplayString(_ctx.$t("Start Recording")), 1),
                                                                    _createElementVNode("div", null, _toDisplayString(_ctx.$t("Screen sharing is ready.")) + " " + _toDisplayString(_ctx.$t("Start recording to open a new tab.")) + " " + _toDisplayString(_ctx.$t("Come back here to save once you're finished.")), 1),
                                                                    _createElementVNode("button", {
                                                                        type: "button",
                                                                        class: "btn btn-lg btn-record",
                                                                        onClick: openWindow
                                                                    }, [
                                                                        _createTextVNode(_toDisplayString(_ctx.$t("Record")) + " ", 1),
                                                                        _createVNode(SvgIcon, { icon: "icon-empty-circle" })
                                                                    ])
                                                                ], 64))
                                                        ]))
                                                        : (_openBlock(), _createElementBlock("button", {
                                                            key: 1,
                                                            type: "button",
                                                            class: "btn btn-lg btn-primary",
                                                            onClick: startScreenSharing
                                                        }, _toDisplayString(_ctx.$t("Share Screen")), 1))
                                                ]))
                                                : (!isScreenPreview.value)
                                                    ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
                                                        _createElementVNode("h3", null, _toDisplayString(_ctx.$t("Screen is being recorded")), 1),
                                                        _createElementVNode("button", {
                                                            type: "button",
                                                            class: "btn btn-lg btn-primary",
                                                            onClick: startPreviewScreenSharing
                                                        }, _toDisplayString(_ctx.$t("Preview")), 1),
                                                        _createElementVNode("div", null, [
                                                            _createElementVNode("button", {
                                                                type: "button",
                                                                class: "rec-screen-overlay__message__link",
                                                                onClick: newScreen,
                                                                innerHTML: _ctx.$t('or {0}change screen{1}', ['<u>', '</u>'])
                                                            }, null, 8, _hoisted_15)
                                                        ])
                                                    ]))
                                                    : (isScreenPreview.value)
                                                        ? (_openBlock(), _createElementBlock("div", _hoisted_16, [
                                                            _createElementVNode("div", {
                                                                class: "rec-screen-overlay__preview__close",
                                                                onClick: stopPreviewScreenSharing
                                                            }),
                                                            _createElementVNode("div", _hoisted_17, [
                                                                _createElementVNode("button", {
                                                                    type: "button",
                                                                    class: "btn btn-lg",
                                                                    onClick: stopPreviewScreenSharing
                                                                }, [
                                                                    _createVNode(SvgIcon, { icon: "icon-eye-hollow-no" }),
                                                                    _createTextVNode(" " + _toDisplayString(_ctx.$t("Hide Preview")), 1)
                                                                ])
                                                            ])
                                                        ]))
                                                        : _createCommentVNode("", true)
                                        ]))
                                        : _createCommentVNode("", true)
                                ], 2),
                                (_ctx.mobile && !_unref(isRecording) && !isScreen.value)
                                    ? (_openBlock(), _createElementBlock("button", {
                                        key: 0,
                                        type: "button",
                                        class: "rec-recorder__upload-option",
                                        "aria-label": _ctx.$t('Upload an existing video or audio recording from your device'),
                                        onClick: handleUploadOptionClick
                                    }, [
                                        _createVNode(SvgIcon, { icon: "icon-upload" }),
                                        _createTextVNode(" " + _toDisplayString(_ctx.$t("Upload an existing recording")), 1)
                                    ], 8, _hoisted_18))
                                    : _createCommentVNode("", true),
                                _createElementVNode("div", _hoisted_19, [
                                    (!isScreen.value && !_unref(isRecording))
                                        ? (_openBlock(), _createBlock(RecordButton, {
                                            key: 0,
                                            ref_key: "recordButtonRef",
                                            ref: recordButtonRef,
                                            variant: "record",
                                            icon: "icon-circle",
                                            label: _ctx.$t('Record'),
                                            tooltip: _ctx.$t('Record'),
                                            disabled: !_unref(mediaStream),
                                            onClick: _unref(startRecording)
                                        }, null, 8, ["label", "tooltip", "disabled", "onClick"]))
                                        : _createCommentVNode("", true),
                                    (_unref(isRecording))
                                        ? (_openBlock(), _createBlock(RecordButton, {
                                            key: 1,
                                            ref_key: "pauseButtonRef",
                                            ref: pauseButtonRef,
                                            pulse: "",
                                            loading: recordingPaused.value,
                                            variant: "pause",
                                            icon: "icon-pause-3",
                                            label: _ctx.$t('Pause'),
                                            tooltip: _ctx.$t('Pause'),
                                            onClick: doPause
                                        }, null, 8, ["loading", "label", "tooltip"]))
                                        : _createCommentVNode("", true)
                                ])
                            ], 512), [
                                [_vShow, inRecordingMode.value]
                            ]),
                            (!inRecordingMode.value)
                                ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                    _createVNode(MediaPlayer, {
                                        "is-playing": isPlaying.value,
                                        "onUpdate:isPlaying": _cache[0] || (_cache[0] = ($event) => ((isPlaying).value = $event)),
                                        "current-time": playbackTimer.value,
                                        "onUpdate:currentTime": _cache[1] || (_cache[1] = ($event) => ((playbackTimer).value = $event)),
                                        "segment-recordings": _unref(segmentRecordings),
                                        mobile: _ctx.mobile,
                                        type: props.type,
                                        "media-id": _unref(uuid),
                                        "mime-type": mimeType.value
                                    }, null, 8, ["is-playing", "current-time", "segment-recordings", "mobile", "type", "media-id", "mime-type"]),
                                    (!isVideoPlayback.value)
                                        ? (_openBlock(), _createElementBlock("div", _hoisted_20, [
                                            _createElementVNode("h3", null, [
                                                (isPlaying.value)
                                                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                                        _createTextVNode(_toDisplayString(_ctx.$t("Playing Recording...")), 1)
                                                    ], 64))
                                                    : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                                        _createTextVNode(_toDisplayString(_ctx.$t("Recording Paused")), 1)
                                                    ], 64))
                                            ]),
                                            _createElementVNode("div", _hoisted_21, [
                                                _createElementVNode("div", null, [
                                                    _createVNode(RecordButton, {
                                                        backdrop: "",
                                                        variant: "reset",
                                                        icon: "icon-reset-2",
                                                        label: _ctx.$t('Start Over'),
                                                        disabled: isPlaying.value || _unref(savingSubmitted),
                                                        onClick: resetRecording
                                                    }, null, 8, ["label", "disabled"]),
                                                    (!isPlaying.value)
                                                        ? (_openBlock(), _createBlock(RecordButton, {
                                                            key: 0,
                                                            backdrop: "",
                                                            variant: "play",
                                                            icon: "icon-play-2",
                                                            label: _ctx.$t('Play'),
                                                            disabled: _unref(savingSubmitted) || noAudio.value,
                                                            onClick: startPlayback
                                                        }, null, 8, ["label", "disabled"]))
                                                        : _createCommentVNode("", true),
                                                    (isPlaying.value)
                                                        ? (_openBlock(), _createBlock(RecordButton, {
                                                            key: 1,
                                                            backdrop: "",
                                                            variant: "stop",
                                                            icon: "icon-square",
                                                            label: _ctx.$t('Stop'),
                                                            onClick: stopPlayback
                                                        }, null, 8, ["label"]))
                                                        : _createCommentVNode("", true)
                                                ]),
                                                _createElementVNode("div", null, [
                                                    _createVNode(RecordButton, {
                                                        backdrop: "",
                                                        variant: "record",
                                                        icon: "icon-circle",
                                                        label: _ctx.$t('Continue'),
                                                        disabled: isPlaying.value || _unref(savingSubmitted) || atMax.value,
                                                        onClick: doContinue
                                                    }, null, 8, ["label", "disabled"]),
                                                    _createVNode(RecordButton, {
                                                        backdrop: "",
                                                        variant: "save",
                                                        icon: "icon-check-4",
                                                        label: _ctx.$t('Save'),
                                                        disabled: belowMin.value || isPlaying.value || _unref(savingSubmitted) || !_unref(recordingReady),
                                                        onClick: _unref(saveRecording)
                                                    }, null, 8, ["label", "disabled", "onClick"])
                                                ])
                                            ]),
                                            (isScreenSharing.value)
                                                ? (_openBlock(), _createElementBlock("div", _hoisted_22, [
                                                    _createElementVNode("button", {
                                                        type: "button",
                                                        class: "rec-recorder__pause-overlay__link",
                                                        onClick: newScreen,
                                                        innerHTML: _ctx.$t('or {0}change screen{1}', ['<u>', '</u>'])
                                                    }, null, 8, _hoisted_23)
                                                ]))
                                                : _createCommentVNode("", true),
                                            (!_unref(recordingReady) && !isPlaying.value)
                                                ? (_openBlock(), _createElementBlock("div", _hoisted_24, [
                                                    _createElementVNode("span", null, _toDisplayString(_ctx.$t("Uploading...")) + " " + _toDisplayString(_unref(uploadProgress)) + "%", 1)
                                                ]))
                                                : _createCommentVNode("", true)
                                        ]))
                                        : _createCommentVNode("", true)
                                ], 64))
                                : _createCommentVNode("", true),
                            (blurHover.value)
                                ? (_openBlock(), _createElementBlock("div", _hoisted_25, [
                                    (_openBlock(), _createElementBlock(_Fragment, null, _renderList(blurAmounts, (amount) => {
                                        return _createElementVNode("span", {
                                            key: amount,
                                            class: _normalizeClass(blurLineClasses.value(amount))
                                        }, null, 2);
                                    }), 64))
                                ]))
                                : _createCommentVNode("", true),
                            (showBlurButton.value)
                                ? _withDirectives((_openBlock(), _createElementBlock("button", {
                                    key: 2,
                                    type: "button",
                                    class: _normalizeClass(blurButtonClasses.value),
                                    "aria-label": blurLabel.value,
                                    onMouseover: blurMouseOver,
                                    onMouseleave: blurMouseLeave,
                                    onFocus: blurMouseOver,
                                    onBlur: blurMouseLeave,
                                    onClick: cycleBlurAmount
                                }, [
                                    _createVNode(SvgIcon, { icon: blurIcon.value }, null, 8, ["icon"])
                                ], 42, _hoisted_26)), [
                                    [
                                        _unref(vTooltip),
                                        blurLabel.value,
                                        void 0,
                                        { e: true }
                                    ]
                                ])
                                : _createCommentVNode("", true)
                        ], 2),
                        _createElementVNode("div", {
                            class: _normalizeClass({ 'rec-recorder__foot': true, 'rec-recorder__foot--playback': isVideoPlayback.value })
                        }, [
                            (isVideoPlayback.value)
                                ? (_openBlock(), _createElementBlock("div", _hoisted_27, [
                                    (isPlaying.value)
                                        ? (_openBlock(), _createBlock(RecordButton, {
                                            key: 0,
                                            variant: "pause",
                                            icon: "icon-pause-3",
                                            size: "medium",
                                            label: _ctx.$t('Pause'),
                                            tooltip: _ctx.$t('Pause'),
                                            onClick: pausePlayback
                                        }, null, 8, ["label", "tooltip"]))
                                        : (_openBlock(), _createBlock(RecordButton, {
                                            key: 1,
                                            variant: "play",
                                            icon: "icon-play-2",
                                            size: "medium",
                                            label: _ctx.$t('Play'),
                                            tooltip: _ctx.$t('Play'),
                                            onClick: startPlayback
                                        }, null, 8, ["label", "tooltip"])),
                                    _createVNode(RecordButton, {
                                        variant: "stop",
                                        icon: "icon-square",
                                        size: "medium",
                                        label: _ctx.$t('Stop'),
                                        tooltip: _ctx.$t('Stop'),
                                        onClick: stopPlayback
                                    }, null, 8, ["label", "tooltip"])
                                ]))
                                : (_openBlock(), _createElementBlock("div", _hoisted_28, [
                                    _createVNode(DeviceInputSelect, {
                                        muted: muted.value,
                                        "onUpdate:muted": _cache[2] || (_cache[2] = ($event) => ((muted).value = $event)),
                                        "input-device-id": _unref(audioInputDeviceId),
                                        "onUpdate:inputDeviceId": _cache[3] || (_cache[3] = ($event) => (_isRef(audioInputDeviceId) ? (audioInputDeviceId).value = $event : null)),
                                        "audio-meter": _unref(audioMeter),
                                        inputs: audioInputs.value,
                                        disabled: noAudioDevice.value,
                                        type: _unref(MediaSource).Audio
                                    }, null, 8, ["muted", "input-device-id", "audio-meter", "inputs", "disabled", "type"]),
                                    (props.type === _unref(MediaSource).Video)
                                        ? (_openBlock(), _createBlock(DeviceInputSelect, {
                                            key: 0,
                                            muted: cameraDisabled.value,
                                            "onUpdate:muted": _cache[4] || (_cache[4] = ($event) => ((cameraDisabled).value = $event)),
                                            "input-device-id": _unref(videoInputDeviceId),
                                            "onUpdate:inputDeviceId": _cache[5] || (_cache[5] = ($event) => (_isRef(videoInputDeviceId) ? (videoInputDeviceId).value = $event : null)),
                                            backgroundBlurAmount: backgroundBlurAmount.value,
                                            "onUpdate:backgroundBlurAmount": _cache[6] || (_cache[6] = ($event) => ((backgroundBlurAmount).value = $event)),
                                            inputs: videoInputs.value,
                                            disabled: noVideo.value,
                                            "prevent-change": _ctx.noVideoDisable,
                                            type: _unref(MediaSource).Video
                                        }, null, 8, ["muted", "input-device-id", "backgroundBlurAmount", "inputs", "disabled", "prevent-change", "type"]))
                                        : _createCommentVNode("", true)
                                ])),
                            _createElementVNode("div", _hoisted_29, [
                                _createVNode(RecorderDuration, {
                                    "recorded-seconds": _unref(recordedSeconds),
                                    minimum: _ctx.minDuration,
                                    maximum: _ctx.maxDuration,
                                    "below-min": belowMin.value,
                                    "at-max": atMax.value,
                                    "is-recording-paused": recordingPaused.value,
                                    "playback-seconds": playbackTimer.value,
                                    playback: isPlaying.value,
                                    onPassLimitMessage: handleLimitMessage
                                }, null, 8, ["recorded-seconds", "minimum", "maximum", "below-min", "at-max", "is-recording-paused", "playback-seconds", "playback"])
                            ])
                        ], 2)
                    ], 2),
                    _withDirectives((_openBlock(), _createElementBlock("button", {
                        type: "button",
                        class: "rec-recorder__close-x",
                        "aria-label": _ctx.$t('Cancel'),
                        onClick: cancelRecorder
                    }, [
                        _createVNode(SvgIcon, { icon: "icon-close-3" })
                    ], 8, _hoisted_30)), [
                        [_unref(vTooltip), _ctx.$t('Cancel')]
                    ])
                ], 2),
                _createVNode(_unref(DiscardRecordingModal), {
                    modelValue: warningModal.value,
                    "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event) => ((warningModal).value = $event)),
                    onCancel: _unref(cancelDraftDiscard),
                    onConfirm: _unref(confirmDraftDiscard)
                }, null, 8, ["modelValue", "onCancel", "onConfirm"])
            ], 64));
        };
    }
});
